/**
 * The shade report in the editor's side panel: per face and per panel, solar access, TOF and TSRF and the yearly
 * production, following the edits live (panels sampled on the analysis grids; unmoved panels keep the layout's
 * yield). The saved version's report is also available as JSON (`…/analysis/shade-report`) for the proposal,
 * where `audience=homeowner` leaves out the method and installer-only factors.
 */
import { Download } from "lucide-react";
import { useMemo, useState } from "react";
import { rawFetch } from "../../api/client";
import { Button } from "../../components/ui";
import { useIntlShape, useT } from "../../i18n/I18nProvider";
import type { LiveRoof } from "../roof.ts";
import type { AnalysisData } from "./data.ts";
import { faceSummaries, type PanelYield } from "./panels.ts";

/** TSRF bands installers use for incentive programmes (e.g. Energy Trust: 80 % per array). */
function band(v: number): string {
  if (!Number.isFinite(v)) return "";
  return v >= 0.8 ? "good" : v >= 0.7 ? "fair" : "poor";
}

export function ShadeReportPanel({
  designId,
  version,
  shownVersion,
  data,
  loading,
  roof,
  yields,
  dirty,
  selected,
  onSelectPanel,
}: {
  designId: string;
  version: number;
  /** The number the editor shows (the file is named with it); `version` is the stored one the API takes. */
  shownVersion: number;
  data: AnalysisData | null;
  loading: boolean;
  roof: LiveRoof;
  yields: PanelYield[] | null;
  dirty: boolean;
  selected: Set<string>;
  onSelectPanel: (key: string) => void;
}) {
  const t = useT();
  const intl = useIntlShape();
  const pct = (v: number | null | undefined) => (v != null && Number.isFinite(v) ? intl.formatNumber(v, { style: "percent", maximumFractionDigits: 0 }) : "—");
  const kwh = (v: number) => (Number.isFinite(v) ? intl.formatNumber(Math.round(v)) : "—");
  const [showPanels, setShowPanels] = useState(false);
  const faces = useMemo(() => (yields ? faceSummaries(yields) : new Map()), [yields]);
  const labels = useMemo(() => new Map(roof.faces.map((f) => [f.id, f.label])), [roof]);
  const numbered = useMemo(() => {
    const order = new Map<string, number>();
    (yields ?? []).forEach((y, i) => order.set(y.key, i + 1));
    return order;
  }, [yields]);
  if (loading) return <p className="muted small">{t("editor.shadeReport.computing")}</p>;
  if (!data || !yields) return <p className="muted small">{t("editor.shadeReport.unavailable")}</p>;
  const total = yields.reduce((s, y) => s + (Number.isFinite(y.kwh) ? y.kwh : 0), 0);
  const access = yields.map((y) => y.access).filter(Number.isFinite);
  const tsrf = yields.map((y) => y.tsrf).filter(Number.isFinite);
  const mean = (v: number[]) => (v.length ? v.reduce((a, b) => a + b, 0) / v.length : NaN);
  const download = async (audience: "installer" | "homeowner") => {
    const r = await rawFetch(`/api/v1/designs/${designId}/versions/${version}/analysis/shade-report?audience=${audience}`);
    const blob = new Blob([JSON.stringify(await r.json(), null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `shade-report-v${shownVersion}-${audience}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <section className="stack an-report">
      <div className="side-card">
        <b>{t("editor.shadeReport.title")}</b>
        <dl className="kv">
          <dt>{t("editor.shadeReport.panels")}</dt>
          <dd>{intl.formatNumber(yields.length)}</dd>
          <dt>{t("editor.shadeReport.production")}</dt>
          <dd>{t("editor.shadeReport.kwhPerYear", { value: kwh(total) })}</dd>
          <dt>{t("editor.layers.access")}</dt>
          <dd>{pct(mean(access))}</dd>
          <dt>{t("editor.layers.tsrf")}</dt>
          <dd>
            {pct(mean(tsrf))} <span className="muted small">{t("editor.shadeReport.lowestPanel", { value: pct(tsrf.length ? Math.min(...tsrf) : NaN) })}</span>
          </dd>
        </dl>
        <p className="muted small">
          {t("editor.shadeReport.method")}
          {dirty && ` ${t("editor.shadeReport.unsavedNote")}`}
        </p>
      </div>
      <table className="face-table an-table">
        <thead>
          <tr>
            <th>{t("editor.shadeReport.face")}</th>
            <th className="num">{t("editor.shadeReport.panels")}</th>
            <th className="num" title={t("editor.layers.access")}>{t("editor.shadeReport.accessShort")}</th>
            <th className="num" title={t("editor.shadeReport.tofTitle")}>{t("editor.shadeReport.tof")}</th>
            <th className="num">{t("editor.layers.tsrf")}</th>
            <th className="num">{t("editor.layers.kwhPerYear")}</th>
          </tr>
        </thead>
        <tbody>
          {roof.faces.map((f) => {
            const s = faces.get(f.id);
            const server = data.faces.get(f.id);
            const faceTsrf = s?.tsrf ?? server?.tsrf ?? NaN;
            return (
              <tr key={f.id}>
                <td>
                  {f.label}
                  {f.edited ? " ✎" : ""}
                </td>
                <td className="num">{s?.panels ?? 0}</td>
                <td className="num">{pct(s?.access ?? server?.solar_access)}</td>
                <td className="num">{f.edited ? t("editor.shadeReport.tofAfterSave") : pct(server?.tof)}</td>
                <td className={`num tsrf ${band(faceTsrf)}`}>{pct(faceTsrf)}</td>
                <td className="num">{s ? kwh(s.kwh) : "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="muted small">{t("editor.shadeReport.footnote", { threshold: intl.formatNumber(0.8, { style: "percent" }) })}</p>
      <Button size="sm" variant="ghost" onClick={() => setShowPanels((v) => !v)}>
        {showPanels ? t("editor.shadeReport.hidePanels") : t("editor.shadeReport.showPanels", { count: yields.length })}
      </Button>
      {showPanels && (
        <table className="face-table an-table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t("editor.shadeReport.face")}</th>
              <th className="num">{t("editor.shadeReport.accessShort")}</th>
              <th className="num">{t("editor.layers.tsrf")}</th>
              <th className="num">{t("editor.layers.kwhPerYear")}</th>
            </tr>
          </thead>
          <tbody>
            {yields.map((y) => (
              <tr key={y.key} className={selected.has(y.key) ? "selected" : ""} onClick={() => onSelectPanel(y.key)}>
                <td>{numbered.get(y.key)}</td>
                <td>{labels.get(y.face_id) ?? "—"}</td>
                <td className="num">{pct(y.access)}</td>
                <td className={`num tsrf ${band(y.tsrf)}`}>{pct(y.tsrf)}</td>
                <td className="num">
                  {kwh(y.kwh)}
                  {y.source === "estimate" ? "*" : ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row wrap">
        <Button size="sm" icon={<Download size={14} />} onClick={() => download("installer")} disabled={dirty} title={dirty ? t("editor.shadeReport.saveFirst") : undefined}>
          {t("editor.shadeReport.downloadJson")}
        </Button>
        <Button size="sm" variant="ghost" icon={<Download size={14} />} onClick={() => download("homeowner")} disabled={dirty}>
          {t("editor.shadeReport.homeownerVersion")}
        </Button>
      </div>
    </section>
  );
}
